import { useState } from "react";
import { Outlet } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { TopNav } from "./TopNav";
import { NotificationPanel } from "./NotificationPanel";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../hooks/useNotifications";
import api from "../../services/api";
import { ErrorBoundary } from "../common/ErrorBoundary";

/**
 * AppShell — authenticated layout wrapper.
 *
 * Renders:
 *  - <Sidebar>  on the left (fixed on desktop, slide-in drawer on mobile)
 *  - <TopNav>   at the top
 *  - <Outlet>   for the current page content
 *  - <NotificationPanel>  slide-out from the right
 *
 * Notifications are wired to the real-time `useNotifications` hook which
 * listens to Firestore for unread notifications filtered by the user's role.
 */
export function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);

  const { role } = useAuth();
  const { notifications, unreadCount } = useNotifications(role);

  async function handleMarkRead(id: string) {
    try {
      await api.patch(`/api/notifications/${id}/read`);
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  }

  async function handleMarkAllRead() {
    const unread = notifications.filter((n) => !n.isRead);
    try {
      await Promise.all(
        unread.map((n) => api.patch(`/api/notifications/${n.id}/read`)),
      );
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* ── Main column (TopNav + page content) ─────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopNav
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          unreadCount={unreadCount}
          onNotificationBellClick={() =>
            setNotificationPanelOpen((prev) => !prev)
          }
        />

        {/* Page content */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8"
          tabIndex={-1}
        >
          <ErrorBoundary level="page">
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* ── Notification slide-out panel ────────────────────────────── */}
      <NotificationPanel
        isOpen={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
        notifications={notifications}
        onMarkRead={handleMarkRead}
        onMarkAllRead={handleMarkAllRead}
      />
    </div>
  );
}
